import React, { useState, useEffect } from 'react';
// Force rebuild
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  TablePagination,
  TableSortLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  useMediaQuery,
  useTheme,
  SelectChangeEvent,
  Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { collection, getDocs, deleteDoc, doc, query, where, orderBy as firestoreOrderBy, updateDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../../config/firebase';
import { Obra } from '../../types';
import ObraForm from './ObraForm';
import { useAuth } from '../../context/AuthContext';
import { usePermisos } from '../../hooks/usePermisos';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

interface Column {
  id: keyof Obra;
  label: string;
  sortable?: boolean;
}

const columns: Column[] = [
  { id: 'nombre', label: 'Nombre', sortable: true },
  { id: 'ubicacion', label: 'Ubicación', sortable: true },
  { id: 'estado', label: 'Estado', sortable: true },
  { id: 'fechaInicio', label: 'Fecha Inicio', sortable: true }
];

const ObrasTable: React.FC = () => {
  const { usuario } = useAuth();
  const { verificarPermisosObra } = usePermisos(usuario);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Estados
  const [obras, setObras] = useState<Obra[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openForm, setOpenForm] = useState(false);
  const [selectedObra, setSelectedObra] = useState<Obra | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' });

  // Estados de paginación y ordenamiento
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderByField, setOrderByField] = useState<keyof Obra>('nombre');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  // Default filter to 'activa' so completed items are hidden by default
  const [filtroEstado, setFiltroEstado] = useState<string>('activa');

  const fetchObras = async () => {
    setLoading(true);
    setError(null);
    try {
      const obrasRef = collection(db, 'obras');
      // Fetch ALL obras to handle filtering client-side
      const q = query(obrasRef);
      const querySnapshot = await getDocs(q);

      const obrasData = querySnapshot.docs
        .map(doc => ({
          ...doc.data(),
          id: doc.id,
          fechaInicio: doc.data().fechaInicio?.toDate ? doc.data().fechaInicio.toDate() : new Date(doc.data().fechaInicio),
          fechaLimite: doc.data().fechaLimite?.toDate ? doc.data().fechaLimite.toDate() : new Date(doc.data().fechaLimite || Date.now()),
        })) as Obra[];

      let filteredObras = obrasData;

      // 1. Permission Filter
      if (usuario && usuario.role !== 'ADMIN' && !usuario.puedeVerTodasLasObras) {
        filteredObras = filteredObras.filter(obra => usuario.obrasAsignadas?.includes(obra.id));
      }

      // 2. Status Filter (Case Insensitive)
      if (filtroEstado !== 'todas') {
        filteredObras = filteredObras.filter(obra =>
          obra.estado?.toLowerCase() === filtroEstado.toLowerCase()
        );
      }

      // 3. Sorting
      filteredObras.sort((a, b) => {
        let valA = a[orderByField];
        let valB = b[orderByField];

        // Handle string comparison (case-insensitive)
        if (typeof valA === 'string' && typeof valB === 'string') {
          return order === 'asc'
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        }

        // Handle dates or numbers
        if (valA < valB) return order === 'asc' ? -1 : 1;
        if (valA > valB) return order === 'asc' ? 1 : -1;
        return 0;
      });

      // 4. Pagination
      const start = page * rowsPerPage;
      const paginatedData = filteredObras.slice(start, start + rowsPerPage);

      setObras(paginatedData);
    } catch (error) {
      console.error(error);
      setError('Error al obtener las obras. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchObras();
  }, [page, rowsPerPage, orderByField, order, filtroEstado]);

  const handleEdit = (obra: Obra) => {
    if (!verificarPermisosObra(obra.id)) {
      setSnackbar({
        open: true,
        message: 'No tienes permisos para editar esta obra',
        severity: 'error'
      });
      return;
    }
    setSelectedObra(obra);
    setOpenForm(true);
  };

  const handleComplete = async (obra: Obra) => {
    if (!verificarPermisosObra(obra.id)) {
      setSnackbar({
        open: true,
        message: 'No tienes permisos para modificar esta obra',
        severity: 'error'
      });
      return;
    }

    if (window.confirm(`⚠️ ¡ADVERTENCIA!\n\n¿Estás seguro de que quieres marcar la obra "${obra.nombre}" como completada?\n\n- Se descargará un archivo .zip con todas las fotos de la obra.\n- Se ELIMINARÁN todas las tareas, materiales, herramientas, fotos y documentos asociados a esta obra en todas las pestañas.\n\nEsta acción de borrado NO se puede deshacer.`)) {
      try {
        setLoading(true);
        setSnackbar({
          open: true,
          message: 'Procesando completado de obra... Por favor no cierre la ventana.',
          severity: 'info'
        });

        // 1. Zipping Photos
        const fotosRef = collection(db, 'fotos');
        const fotosQuery = query(fotosRef, where('obraId', '==', obra.id));
        const fotosSnapshot = await getDocs(fotosQuery);
        
        if (!fotosSnapshot.empty) {
          const zip = new JSZip();
          const imgFolder = zip.folder("fotos_" + obra.nombre.replace(/[^a-z0-9]/gi, '_'));
          
          const fetchPromises = fotosSnapshot.docs.map(async (docSnapshot, index) => {
            const fotoData = docSnapshot.data();
            if (fotoData.url) {
              try {
                const response = await fetch(fotoData.url);
                const blob = await response.blob();
                // Get extension from URL or fallback
                let extension = 'jpg';
                if (fotoData.url.toLowerCase().includes('.png')) extension = 'png';
                if (fotoData.url.toLowerCase().includes('.jpeg')) extension = 'jpeg';
                
                // Use description as name or fallback, append index to guarantee uniqueness
                const safeDesc = (fotoData.descripcion || 'foto').replace(/[^a-z0-9\-_]/gi, '_');
                const filename = `${safeDesc}_${index + 1}.${extension}`;
                
                imgFolder?.file(filename, blob);
              } catch (e) {
                console.error("Error fetching photo for zip:", e);
              }
            }
          });
          
          await Promise.all(fetchPromises);
          
          const content = await zip.generateAsync({ type: "blob" });
          saveAs(content, `Fotos_${obra.nombre.replace(/[^a-z0-9]/gi, '_')}.zip`);
        }

        // 2. Delete all related data

        // a. Delete materiales
        const materialesRef = collection(db, 'materiales');
        const materialesQuery = query(materialesRef, where('obraId', '==', obra.id));
        const materialesSnapshot = await getDocs(materialesQuery);
        const materialesPromises = materialesSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(materialesPromises);

        // b. Delete tareas
        const tareasRef = collection(db, 'tareas');
        const tareasQuery = query(tareasRef, where('obraId', '==', obra.id));
        const tareasSnapshot = await getDocs(tareasQuery);
        const tareasPromises = tareasSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(tareasPromises);

        // c. Delete herramientas
        const herramientasRef = collection(db, 'herramientas');
        const herramientasQuery = query(herramientasRef, where('obraId', '==', obra.id));
        const herramientasSnapshot = await getDocs(herramientasQuery);
        const herramientasPromises = herramientasSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(herramientasPromises);

        // d. Delete fotos (we already have fotosSnapshot, we just need to delete from storage and db)
        const fotosDelPromises = fotosSnapshot.docs.map(async (docSnap) => {
          const fotoData = docSnap.data();
          if (fotoData.url) {
            const imageRef = ref(storage, fotoData.url);
            try {
              await deleteObject(imageRef);
            } catch (error) {
              console.error('Error deleting foto file:', error);
            }
          }
          return deleteDoc(docSnap.ref);
        });
        await Promise.all(fotosDelPromises);

        // e. Delete documentos
        const documentosRef = collection(db, 'documentos');
        const documentosQuery = query(documentosRef, where('obra', '==', obra.id));
        const documentosSnapshot = await getDocs(documentosQuery);
        const documentosPromises = documentosSnapshot.docs.map(async (docSnap) => {
          const documentoData = docSnap.data();
          if (documentoData.url) {
            const fileRef = ref(storage, documentoData.url);
            try {
              await deleteObject(fileRef);
            } catch (error) {
              console.error('Error deleting documento file:', error);
            }
          }
          return deleteDoc(docSnap.ref);
        });
        await Promise.all(documentosPromises);

        // f. Delete subcollections: items_presupuesto
        const itemsPresupuestoRef = collection(db, `obras/${obra.id}/items_presupuesto`);
        const itemsPresupuestoSnapshot = await getDocs(query(itemsPresupuestoRef));
        const itemsPresupuestoPromises = itemsPresupuestoSnapshot.docs.map(docSnap => deleteDoc(docSnap.ref));
        await Promise.all(itemsPresupuestoPromises);

        // g. Delete subcollections: rubros
        const rubrosRef = collection(db, `obras/${obra.id}/rubros`);
        const rubrosSnapshot = await getDocs(query(rubrosRef));
        const rubrosPromises = rubrosSnapshot.docs.map(docSnap => deleteDoc(docSnap.ref));
        await Promise.all(rubrosPromises);

        // 3. Update the obra status
        await updateDoc(doc(db, 'obras', obra.id), {
          estado: 'completada'
        });

        await fetchObras();
        setSnackbar({
          open: true,
          message: 'Obra marcada como completada y datos purgados exitosamente',
          severity: 'success'
        });
      } catch (error) {
        console.error("Error completing obra:", error);
        setSnackbar({
          open: true,
          message: 'Error al completar la obra',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (!verificarPermisosObra(id)) {
      setSnackbar({
        open: true,
        message: 'No tienes permisos para eliminar esta obra',
        severity: 'error'
      });
      return;
    }

    if (window.confirm('⚠️ ¡ADVERTENCIA! ¿Estás seguro de que quieres ELIMINAR PERMANENTEMENTE esta obra?\n\nEsta acción NO se puede deshacer. Se borrarán todos los materiales, fotos y documentos asociados para siempre.')) {
      try {
        setLoading(true); // Show loading while deleting
        // 1. Delete materiales
        const materialesRef = collection(db, 'materiales');
        const materialesQuery = query(materialesRef, where('obraId', '==', id));
        const materialesSnapshot = await getDocs(materialesQuery);
        const materialesPromises = materialesSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(materialesPromises);

        // 2. Delete fotos
        const fotosRef = collection(db, 'fotos');
        const fotosQuery = query(fotosRef, where('obraId', '==', id));
        const fotosSnapshot = await getDocs(fotosQuery);
        const fotosPromises = fotosSnapshot.docs.map(async (doc) => {
          const fotoData = doc.data();
          // Delete storage file
          if (fotoData.url) {
            const imageRef = ref(storage, fotoData.url);
            try {
              await deleteObject(imageRef);
            } catch (error) {
              console.error('Error deleting foto file:', error);
            }
          }
          // Delete documento document
          return deleteDoc(doc.ref);
        });
        await Promise.all(fotosPromises);

        // 3. Delete documentos
        const documentosRef = collection(db, 'documentos');
        const documentosQuery = query(documentosRef, where('obra', '==', id)); // Changed obraId to obra
        const documentosSnapshot = await getDocs(documentosQuery);
        const documentosPromises = documentosSnapshot.docs.map(async (doc) => {
          const documentoData = doc.data();
          // Delete storage file
          if (documentoData.url) {
            const fileRef = ref(storage, documentoData.url);
            try {
              await deleteObject(fileRef);
            } catch (error) {
              console.error('Error deleting documento file:', error);
            }
          }
          // Delete documento document
          return deleteDoc(doc.ref);
        });
        await Promise.all(documentosPromises);

        // 4. Finally delete the obra
        await deleteDoc(doc(db, 'obras', id));

        await fetchObras();
        setSnackbar({
          open: true,
          message: 'Obra y todos sus datos relacionados fueron eliminados permanentemente',
          severity: 'success'
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Error al eliminar la obra y sus datos relacionados',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFormClose = (wasSuccessful?: boolean) => {
    setOpenForm(false);
    setSelectedObra(null);
    if (wasSuccessful) {
      fetchObras();
      setSnackbar({
        open: true,
        message: selectedObra ? 'Obra actualizada exitosamente' : 'Obra creada exitosamente',
        severity: 'success'
      });
    }
  };

  const handleSort = (property: keyof Obra) => {
    const isAsc = orderByField === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderByField(property);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeFilter = (event: SelectChangeEvent<string>) => {
    setFiltroEstado(event.target.value);
    setPage(0);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Función auxiliar para renderizar el valor de la celda
  const renderCellValue = (obra: Obra, columnId: keyof Obra) => {
    if (columnId === 'estado') {
      return (
        <span style={{
          textTransform: 'capitalize',
          color: obra.estado === 'activa' ? 'green' : obra.estado === 'completada' ? 'blue' : 'gray',
          fontWeight: 'bold'
        }}>
          {obra.estado}
        </span>
      );
    }
    if (columnId === 'fechaInicio') {
      return formatDate(obra[columnId]);
    }
    return obra[columnId];
  };

  return (
    <Box>
      {/* Controles superiores */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenForm(true)}
        >
          Agregar Obra
        </Button>

        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filtrar por Estado</InputLabel>
          <Select
            value={filtroEstado}
            onChange={handleChangeFilter}
            label="Filtrar por Estado"
          >
            <MenuItem value="todas">Todas</MenuItem>
            <MenuItem value="activa">Activa</MenuItem>
            <MenuItem value="completada">Completada</MenuItem>
            <MenuItem value="suspendida">Suspendida</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Mensaje de error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabla con loader */}
      <TableContainer component={Paper}>
        <Table size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{ display: isMobile && column.id !== 'nombre' ? 'none' : 'table-cell' }}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderByField === column.id}
                      direction={orderByField === column.id ? order : 'asc'}
                      onClick={() => handleSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : obras.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center">
                  {filtroEstado === 'todas'
                    ? 'No hay obras registradas'
                    : `No hay obras con estado "${filtroEstado}"`}
                </TableCell>
              </TableRow>
            ) : (
              obras.map((obra) => (
                <TableRow key={obra.id}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      sx={{ display: isMobile && column.id !== 'nombre' ? 'none' : 'table-cell' }}
                    >
                      {renderCellValue(obra, column.id)}
                    </TableCell>
                  ))}
                  <TableCell>
                    <Box sx={{ display: 'flex' }}>
                      {obra.estado !== 'completada' && (
                        <Tooltip title="Marcar como Completada">
                          <IconButton
                            onClick={() => handleComplete(obra)}
                            color="success"
                            size={isMobile ? "small" : "medium"}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title="Editar">
                        <IconButton
                          onClick={() => handleEdit(obra)}
                          color="primary"
                          size={isMobile ? "small" : "medium"}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Eliminar (Permanente)">
                        <IconButton
                          onClick={() => handleDelete(obra.id)}
                          color="error"
                          size={isMobile ? "small" : "medium"}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginación */}
      <TablePagination
        component="div"
        count={obras.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count}`
        }
      />

      {/* Modal de form */}
      {openForm && (
        <ObraForm
          open={openForm}
          onClose={handleFormClose}
          obraToEdit={selectedObra}
        />
      )}

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity as any}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ObrasTable;